import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { MessageCircle, Phone, Sparkles, Star, Shield, Plane, Calendar, MapPin } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import AIChat from "@/components/AIChat";
import { getSpotlight } from "@/lib/destinations";
import { trackLead } from "@/lib/analytics";

// Dedicated Google-Ads conversion landing page.
// Stripped of the main site nav/footer to maximise ad-click conversion:
// - One headline, one CTA above the fold
// - Hero image auto-picked from the destination Spotlight pool (UTM dest param)
// - Inline 3-field form (name / phone / optional dates) → creates a lead + opens WhatsApp
// - Floating "Chat with AI" if the visitor prefers the conversational funnel
//
// URL: `/lp` (alias `/ads` and `/go`)
// Params the page understands:
//   ?dest=Bali                   → hero image + form pre-fill + headline variant
//   ?occasion=Honeymoon          → copy tuning
//   ?headline=Custom%20copy      → override H1 (A/B testing)
//   ?utm_source=google&utm_campaign=summer-bali&utm_medium=cpc&utm_content=ad1
//
// All UTM params flow into the created lead row for campaign attribution.

const WHATSAPP_NUMBER = "919501030117";

const VALUE_PROPS = [
  { icon: Shield,   title: "20 Years, 4.8★ on Google",  body: "264+ reviews from real travellers. Not a marketplace — a curated studio." },
  { icon: Plane,    title: "Direct Flights First",      body: "We book direct flights wherever possible. No surprise layovers." },
  { icon: Sparkles, title: "Offline Negotiated Rates",  body: "10–15% lower than top OTAs — sent in a custom PDF." },
];

const REVIEWS = [
  { name: "Dr. Anand Sharma", quote: "Outstanding Bali honeymoon planning. Every hotel, every transfer — flawless.", rating: 5 },
  { name: "Neelam K.",        quote: "Seamless Dubai family holiday. The direct-flight booking saved us a full day.", rating: 5 },
  { name: "Rajvinder Singh",  quote: "Kashmir 2024 — gorgeous houseboats, punctual drivers. Best agent I've used.", rating: 5 },
];

export default function Landing() {
  const [params] = useSearchParams();
  const destParam = (params.get("dest") || "").trim();
  const occasionParam = (params.get("occasion") || "").trim();
  const headlineOverride = params.get("headline");

  // Pick a hero image from the Spotlight pool if the ad targets a specific destination;
  // otherwise fall back to a striking generic travel photo.
  const hero = useMemo(() => {
    if (destParam) {
      const spot = getSpotlight(destParam);
      if (spot?.hero) return { image: spot.hero, label: spot.name };
    }
    return {
      image: "https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?w=2000&q=80",
      label: "",
    };
  }, [destParam]);

  const utm = useMemo(() => ({
    utm_source: params.get("utm_source") || null,
    utm_medium: params.get("utm_medium") || null,
    utm_campaign: params.get("utm_campaign") || null,
    utm_term: params.get("utm_term") || null,
    utm_content: params.get("utm_content") || null,
  }), [params]);

  const headline = headlineOverride
    ? headlineOverride
    : destParam
    ? `Custom ${destParam} Holidays`
    : "Customised Holidays, Planned by Experience.";

  const subline = occasionParam
    ? `Handpicked ${occasionParam.toLowerCase()} packages from a 20-year travel studio`
    : "20-year studio · 4.8★ on Google · direct flights + offline-negotiated rates";

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [travelDate, setTravelDate] = useState("");
  const [dest, setDest] = useState(destParam);
  const [hp, setHp] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const conversionSent = useRef(false);

  // Fire GA4 / Meta conversion via shared helper. Falls back to no-op when
  // analytics scripts aren't loaded.
  const fireConversion = (leadId) => {
    if (conversionSent.current) return;
    conversionSent.current = true;
    trackLead({
      source: "lp_form",
      destination: dest || null,
      label: "lp_lead",
      extra: { lead_id: leadId || null, occasion: occasionParam || null },
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (hp) return; // honeypot tripped
    if (!name.trim() || !phone.trim()) {
      toast.error("Please share your name and WhatsApp number.");
      return;
    }
    if (phone.replace(/\D/g, "").length < 10) {
      toast.error("Please enter a valid 10-digit number.");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        name: name.trim(),
        phone: phone.replace(/\D/g, ""),
        destination: dest.trim() || null,
        travel_date: travelDate.trim() || null,
        occasion: occasionParam || null,
        source: "google_ads_lp",
        ...utm,
      };
      const { data: lead } = await api.post("/leads", payload);
      fireConversion(lead?.id);

      // Open WhatsApp with everything pre-filled
      const lines = [
        `Hello Vacations Pro — please share my custom itinerary.`,
        ``,
        `Name: ${payload.name}`,
        `Phone: +91 ${payload.phone}`,
        `Destination: ${payload.destination || "—"}`,
        `Travel date: ${payload.travel_date || "—"}`,
        `Occasion: ${payload.occasion || "—"}`,
      ];
      const wa = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(lines.join("\n"))}`;
      window.open(wa, "_blank", "noopener");
      toast.success("Got it! We've opened WhatsApp — a consultant will reach out within 30 minutes.");
      setName(""); setPhone(""); setTravelDate("");
    } catch (err) {
      console.warn("LP lead submission failed", err);
      toast.error(err?.response?.data?.detail || "Couldn't submit — please WhatsApp us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    // Scroll to top on mount — avoids landing on a mid-scroll position after a redirect.
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="lp-page">
      <Helmet>
        <title>{`${headline} | Vacations Pro`}</title>
        <meta name="description" content={subline} />
        {/* Avoid indexing the ad-specific LP so it doesn't cannibalise the main site */}
        <meta name="robots" content="noindex, follow" />
      </Helmet>

      {/* Minimal header — logo + call-only (no nav) */}
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-[var(--vp-border)] px-5 md:px-10 py-3 flex items-center justify-between">
        <a href="/" className="flex-shrink-0" data-testid="lp-logo">
          <img src="/logo-header.png?v=blue3" alt="Vacations Pro" className="h-12 md:h-16 w-auto object-contain" />
        </a>
        <a
          href={`tel:+${WHATSAPP_NUMBER}`}
          className="inline-flex items-center gap-2 bg-[var(--vp-primary)] hover:bg-[#0e1b3d] text-white rounded-full px-4 md:px-5 py-2 md:py-2.5 text-sm font-semibold transition-colors"
          data-testid="lp-call-cta"
        >
          <Phone className="w-4 h-4" />
          <span className="hidden sm:inline">Call +91 95010 30117</span>
          <span className="sm:hidden">Call us</span>
        </a>
      </header>

      {/* Hero + inline form */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={hero.image} alt={hero.label || "Travel"} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/70" />
        </div>
        <div className="relative z-10 grid md:grid-cols-[1.1fr_0.9fr] gap-10 px-6 md:px-12 lg:px-20 pt-12 md:pt-20 pb-14 md:pb-24 max-w-[1280px] mx-auto">
          <div className="text-white">
            <div className="inline-flex items-center gap-1.5 bg-white/15 backdrop-blur border border-white/20 rounded-full px-3 py-1 text-[11px] tracking-[0.2em] uppercase font-semibold">
              <Star className="w-3.5 h-3.5 text-[var(--vp-accent)]" fill="currentColor" />
              4.8 on Google · 264+ reviews
            </div>
            <h1
              className="font-serif-vp text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight mt-5 drop-shadow-[0_2px_24px_rgba(0,0,0,0.5)]"
              data-testid="lp-headline"
            >
              {headline}
            </h1>
            <p className="mt-5 text-base md:text-lg text-white/90 leading-relaxed max-w-[540px]">
              {subline}.
            </p>
            <div className="mt-6 hidden md:flex items-center gap-6 text-[13px] text-white/80">
              <span className="inline-flex items-center gap-1.5"><Shield className="w-4 h-4 text-[var(--vp-accent)]" /> 20-year studio</span>
              <span className="inline-flex items-center gap-1.5"><Plane className="w-4 h-4 text-[var(--vp-accent)]" /> Direct flights</span>
              <span className="inline-flex items-center gap-1.5"><Sparkles className="w-4 h-4 text-[var(--vp-accent)]" /> 10–15% cheaper than top OTAs</span>
            </div>
          </div>

          {/* Lead form */}
          <form
            onSubmit={submit}
            className="bg-white rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.35)] p-5 md:p-7 space-y-3"
            data-testid="lp-form"
          >
            <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[var(--vp-secondary)]">
              Free custom quote
            </div>
            <h2 className="font-serif-vp text-2xl md:text-3xl text-[var(--vp-primary)] leading-snug">
              Get your itinerary on WhatsApp
            </h2>

            {/* Honeypot */}
            <input type="text" name="hp" tabIndex={-1} autoComplete="off" aria-hidden="true"
                   value={hp} onChange={(e) => setHp(e.target.value)} className="hidden" />

            <Field icon={<MapPin className="w-4 h-4" />} label="Destination">
              <input
                type="text" placeholder="e.g. Bali, Kashmir, Dubai"
                value={dest} onChange={(e) => setDest(e.target.value)}
                className="flex-1 bg-transparent outline-none text-[15px] placeholder:text-[var(--vp-muted)]"
                data-testid="lp-dest-input"
              />
            </Field>

            <Field icon={<Sparkles className="w-4 h-4" />} label="Your name">
              <input
                type="text" placeholder="First name" required
                value={name} onChange={(e) => setName(e.target.value)}
                className="flex-1 bg-transparent outline-none text-[15px] placeholder:text-[var(--vp-muted)]"
                data-testid="lp-name-input"
              />
            </Field>

            <Field icon={<Phone className="w-4 h-4" />} label="WhatsApp number">
              <span className="text-[13px] text-[var(--vp-muted)] mr-1">+91</span>
              <input
                type="tel" placeholder="10-digit" required inputMode="numeric" maxLength={10}
                value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                className="flex-1 bg-transparent outline-none text-[15px] placeholder:text-[var(--vp-muted)]"
                data-testid="lp-phone-input"
              />
            </Field>

            <Field icon={<Calendar className="w-4 h-4" />} label="Travel dates (optional)">
              <input
                type="text" placeholder="e.g. March 15 · 5 nights"
                value={travelDate} onChange={(e) => setTravelDate(e.target.value)}
                className="flex-1 bg-transparent outline-none text-[15px] placeholder:text-[var(--vp-muted)]"
                data-testid="lp-date-input"
              />
            </Field>

            <button
              type="submit" disabled={submitting}
              className="w-full bg-[#25D366] hover:bg-[#1ebe5d] text-white rounded-xl py-3.5 text-sm tracking-[0.1em] uppercase font-bold transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
              data-testid="lp-submit-btn"
            >
              <MessageCircle className="w-4 h-4" />
              {submitting ? "Sending…" : "Get my quote on WhatsApp"}
            </button>

            <p className="text-[11px] text-[var(--vp-muted)] text-center mt-2">
              Free AI concierge · Instantly tailored · Verified by human travel experts
            </p>

            <button
              type="button" onClick={() => setShowChat(true)}
              className="w-full mt-1 text-xs text-[var(--vp-primary)] hover:text-[var(--vp-secondary)] underline underline-offset-4"
              data-testid="lp-open-chat"
            >
              Or chat with our AI concierge instead →
            </button>
          </form>
        </div>
      </section>

      {/* Value props */}
      <section className="px-6 md:px-12 lg:px-20 py-14 max-w-[1200px] mx-auto">
        <div className="grid md:grid-cols-3 gap-5" data-testid="lp-value-props">
          {VALUE_PROPS.map(({ icon: Icon, title, body }) => (
            <div key={title} className="bg-[var(--vp-bg)] border border-[var(--vp-border)] rounded-xl p-5">
              <div className="w-10 h-10 rounded-full bg-[var(--vp-primary)]/10 flex items-center justify-center mb-4">
                <Icon className="w-5 h-5 text-[var(--vp-primary)]" strokeWidth={1.6} />
              </div>
              <div className="font-serif-vp text-xl text-[var(--vp-primary)] mb-1 leading-snug">{title}</div>
              <p className="text-sm text-[var(--vp-muted)] leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Reviews */}
      <section className="px-6 md:px-12 lg:px-20 pb-14 max-w-[1200px] mx-auto" data-testid="lp-reviews">
        <div className="flex items-baseline justify-between mb-5">
          <h2 className="font-serif-vp text-2xl md:text-3xl text-[var(--vp-primary)]">
            What travellers say
          </h2>
          <a
            href="https://g.page/r/CaKeJCsGpCWqEAI/review" target="_blank" rel="noopener noreferrer"
            className="text-xs text-[var(--vp-secondary)] hover:underline"
          >
            See all 264 Google reviews →
          </a>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {REVIEWS.map((r) => (
            <div key={r.name} className="bg-white border border-[var(--vp-border)] rounded-xl p-5">
              <div className="flex items-center gap-1 text-[var(--vp-accent)] mb-3">
                {Array.from({ length: r.rating }).map((_, i) => (
                  <Star key={i} className="w-4 h-4" fill="currentColor" strokeWidth={0} />
                ))}
              </div>
              <p className="text-sm text-[var(--vp-text)] leading-relaxed">“{r.quote}”</p>
              <div className="mt-3 text-[11px] tracking-wider uppercase text-[var(--vp-muted)] font-semibold">
                — {r.name}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Final CTA band */}
      <section className="bg-[var(--vp-primary)] text-white px-6 md:px-12 lg:px-20 py-12 text-center">
        <div className="max-w-[720px] mx-auto">
          <h2 className="font-serif-vp text-3xl md:text-4xl leading-tight">
            Ready to plan {destParam || "your trip"}?
          </h2>
          <p className="text-white/75 mt-3 text-sm md:text-base">
            Tap below to chat on WhatsApp — a real human, no chatbot, no waiting queue.
          </p>
          <a
            href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
              `Hi Vacations Pro, I'd like to plan a trip${destParam ? ` to ${destParam}` : ""}.`
            )}`}
            target="_blank" rel="noopener noreferrer"
            onClick={() => trackLead({ source: "lp_final_cta", destination: destParam || null })}
            className="inline-flex items-center gap-2 mt-6 bg-[#25D366] hover:bg-[#1ebe5d] text-white rounded-full px-7 py-3.5 text-sm tracking-[0.1em] uppercase font-bold transition-colors"
            data-testid="lp-final-cta"
          >
            <MessageCircle className="w-4 h-4" />
            Start on WhatsApp
          </a>
        </div>
      </section>

      {/* Minimal footer */}
      <footer className="px-6 md:px-12 py-8 text-center text-xs text-[var(--vp-muted)]">
        © {new Date().getFullYear()} Vacations Pro International Pvt. Ltd. · Customised Holiday Makerz Co.
      </footer>

      <AIChat open={showChat} onOpenChange={setShowChat} destination={dest || undefined} mood={occasionParam || undefined} />
    </div>
  );
}

// Tiny shared input-row wrapper with a left-side icon + label strip.
function Field({ icon, label, children }) {
  return (
    <label className="block border border-[var(--vp-border)] rounded-lg px-3 py-2 bg-white focus-within:border-[var(--vp-primary)] transition-colors">
      <div className="text-[10px] tracking-wider uppercase text-[var(--vp-muted)] font-semibold flex items-center gap-1.5">
        {icon}
        {label}
      </div>
      <div className="flex items-center gap-1 mt-0.5">{children}</div>
    </label>
  );
}
